import React, { useEffect, useRef } from 'react';
import { getErrorMessage } from '@/utils/formatApiError';
import AppButton from './AppButton';
import AppErrorBanner from './AppErrorBanner';

export type AppConfirmDialogProps = {
  title: string;
  lead: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary' | 'warning';
  submitting?: boolean;
  error?: string | unknown;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  children?: React.ReactNode;
  open?: boolean;
};

export default function AppConfirmDialog({
  title,
  lead,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'danger',
  submitting = false,
  error,
  onClose,
  onConfirm,
  children,
  open = true,
}: AppConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const confirmVariant =
    variant === 'primary' ? 'primary' : variant === 'warning' ? 'warning' : 'danger';

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal card card-elevated confirm-destructive-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-confirm-dialog-title"
        data-testid="confirm-destructive-dialog"
      >
        <h2 id="app-confirm-dialog-title">{title}</h2>
        <div className="confirm-destructive-dialog__body">
          {error ? (
            <AppErrorBanner message={error} showDetails={false} className="app-error-banner--inline" />
          ) : null}
          <p className="modal-lead">{lead}</p>
          {children}
        </div>
        <div className="modal-actions confirm-destructive-dialog__actions">
          <AppButton
            ref={cancelRef}
            variant="ghost"
            disabled={submitting}
            onClick={onClose}
          >
            {cancelLabel}
          </AppButton>
          <AppButton
            variant={confirmVariant}
            loading={submitting}
            onClick={() => void onConfirm()}
          >
            {confirmLabel}
          </AppButton>
        </div>
      </div>
    </div>
  );
}

/** @deprecated use error prop directly — kept for callers passing string only */
export function confirmDialogErrorText(error: unknown): string {
  return getErrorMessage(error);
}
